/** Username + password sign-in, the frontend's decisions: every branch the screens take lives here and is tested in `password.test.ts` (vitest has no DOM). The wire contract is `routers/auth.py` and `routers/admin.py`.
 *  Refusals carry a stable `detail` PREFIX; the English prose after it is shown only when no translated sentence exists.
 *  ⛔ Imports nothing from `./api`: `./api` imports `isPasswordChangeRequired` from here, and a cycle would leave one undefined at load. */
import axios from 'axios'

/** `deps.PASSWORD_CHANGE_REQUIRED_DETAIL`'s prefix. A wire contract. */
const PASSWORD_CHANGE_REQUIRED = 'password_change_required:'

/** `services/password_signin.COMPANY_DEACTIVATED`'s prefix. A wire contract. */
const COMPANY_DEACTIVATED = 'company_deactivated:'

/** `services/password_signin.MIN_PASSWORD_CHARS` / `MAX_PASSWORD_BYTES`. The server is the rule; these only spare a round-trip. */
const MIN_PASSWORD_CHARS = 8
export const MAX_PASSWORD_BYTES = 72

/** The sign-in route. A 401 from it is a wrong handle/password, never a dead session: `api.ts` exempts it from the 401 sign-out path. */
export const PASSWORD_SIGN_IN_PATH = '/api/auth/password/sign-in'

/** The emailed-code step that follows a customer administrator's password. Exempt from the 401 sign-out path for the same reason. */
export const PASSWORD_VERIFY_CODE_PATH = '/api/auth/password/verify-code'

/** A customer administrator's forgotten password: ask for a code, then spend it. Exempt from the 401 sign-out path for the same reason. */
export const PASSWORD_FORGOT_PATH = '/api/auth/password/forgot'
export const PASSWORD_RESET_PATH = '/api/auth/password/reset'

/** Is this a 403 telling a password session it must change its password first? Keyed on the PREFIX: an inactive account and a leaver outside their window answer 403 too. */
export function isPasswordChangeRequired(status: number | undefined, detail: unknown): boolean {
  return status === 403 && typeof detail === 'string' && detail.startsWith(PASSWORD_CHANGE_REQUIRED)
}

interface PasswordFlags {
  has_password?: boolean
  password_must_change?: boolean
  impersonated_by_id?: string | null
}

/** Show the forced change-password screen instead of the app?
 *  Not while viewing as somebody: `password/change` acts on the SIGNED-IN person (an administrator with no password), so the admin would be stuck on a screen whose only action fails. */
export function showForcedChange(user: PasswordFlags | null | undefined): boolean {
  return Boolean(user?.password_must_change) && !user?.impersonated_by_id
}

/** Is this refusal the operator having turned the person's company off? */
export function isCompanyDeactivated(detail: unknown): boolean {
  return typeof detail === 'string' && detail.startsWith(COMPANY_DEACTIVATED)
}

/** `deps.current_user`'s refusal for an account switched off or an expired external. A wire contract. */
export function isAccountInactive(detail: unknown): boolean {
  return detail === 'account is inactive'
}

/** My Panel's «Change password» card; same impersonation rule as above. */
export function showChangePasswordCard(user: PasswordFlags | null | undefined): boolean {
  return Boolean(user?.has_password) && !user?.impersonated_by_id
}

type PasswordProblem =
  | 'tooShort'
  | 'tooLong'
  | 'needLower'
  | 'needUpper'
  | 'needDigit'
  | 'needSymbol'
  | 'isUsername'
  | 'hasPersonal'
  | 'hasRun'
  | 'hasSequence'
  | 'mismatch'

/** One line of the live checklist. `common` is not here: the 10,000-word list stays on the server and is checked on save. */
export type PasswordRuleId = 'length' | 'lower' | 'upper' | 'digit' | 'symbol' | 'personal' | 'run' | 'sequence'

export interface PasswordContext {
  username?: string | null
  email?: string | null
  names?: ReadonlyArray<string | null | undefined>
}

const MIN_CONTEXT_PART_CHARS = 3
const MAX_IDENTICAL_RUN = 2
const MAX_SEQUENCE_RUN = 3

/** `password_strength.context_terms`: each source whole, and its words, when at least three characters. */
function contextTerms(ctx: PasswordContext): Set<string> {
  const sources: string[] = []
  if (ctx.username) sources.push(ctx.username)
  if (ctx.email) sources.push(ctx.email.split('@', 1)[0])
  for (const name of ctx.names ?? []) if (name) sources.push(name)
  const terms = new Set<string>()
  for (const source of sources) {
    const folded = source.trim().toLowerCase()
    if ([...folded].length >= MIN_CONTEXT_PART_CHARS) terms.add(folded)
    for (const part of folded.split(/[^\p{L}\p{N}]+/u)) {
      if ([...part].length >= MIN_CONTEXT_PART_CHARS) terms.add(part)
    }
  }
  return terms
}

function hasIdenticalRun(password: string): boolean {
  const chars = [...password.toLowerCase()]
  let run = 1
  for (let i = 1; i < chars.length; i++) {
    run = chars[i] === chars[i - 1] ? run + 1 : 1
    if (run > MAX_IDENTICAL_RUN) return true
  }
  return false
}

function hasSequence(password: string): boolean {
  const chars = [...password.toLowerCase()]
  const alnum = /^[\p{L}\p{N}]$/u
  let step = 0
  let run = 1
  for (let i = 1; i < chars.length; i++) {
    const diff = chars[i].codePointAt(0)! - chars[i - 1].codePointAt(0)!
    if ((diff === 1 || diff === -1) && alnum.test(chars[i - 1]) && alnum.test(chars[i])) {
      run = diff === step ? run + 1 : 2
      step = diff
    } else {
      run = 1
      step = 0
    }
    if (run > MAX_SEQUENCE_RUN) return true
  }
  return false
}

/** The client copy of `password_strength.strength_problem`'s composition and pattern rules, in the server's order, each passing or failing on its own so the dialogs can tick them live. */
export function passwordChecks(password: string, ctx: PasswordContext = {}): { id: PasswordRuleId; ok: boolean }[] {
  const folded = password.toLowerCase()
  return [
    { id: 'length', ok: [...password].length >= MIN_PASSWORD_CHARS },
    { id: 'lower', ok: /\p{Ll}/u.test(password) },
    { id: 'upper', ok: /\p{Lu}/u.test(password) },
    { id: 'digit', ok: /\p{Nd}/u.test(password) },
    { id: 'symbol', ok: /[^\p{L}\p{Nd}]/u.test(password) },
    { id: 'personal', ok: ![...contextTerms(ctx)].some((term) => folded.includes(term)) },
    { id: 'run', ok: !hasIdenticalRun(password) },
    { id: 'sequence', ok: !hasSequence(password) },
  ]
}

const CHECK_PROBLEM: Record<PasswordRuleId, PasswordProblem> = {
  length: 'tooShort',
  lower: 'needLower',
  upper: 'needUpper',
  digit: 'needDigit',
  symbol: 'needSymbol',
  personal: 'hasPersonal',
  run: 'hasRun',
  sequence: 'hasSequence',
}

/** The first rule broken, in the server's order. Maximum counts UTF-8 bytes (bcrypt's 72-byte limit, refused never truncated); `undefined` confirm skips the comparison. The common-password rule is server-only. */
export function passwordProblem(
  password: string,
  username: string | null | undefined,
  confirm?: string,
  extra: Omit<PasswordContext, 'username'> = {},
): PasswordProblem | null {
  if (new TextEncoder().encode(password).length > MAX_PASSWORD_BYTES) return 'tooLong'
  const ctx = { ...extra, username }
  for (const check of passwordChecks(password, ctx)) {
    if (check.ok) continue
    const name = (username ?? '').trim().toLowerCase()
    if (check.id === 'personal' && name && password.trim().toLowerCase() === name) return 'isUsername'
    return CHECK_PROBLEM[check.id]
  }
  if (confirm !== undefined && confirm !== password) return 'mismatch'
  return null
}

/** Every sentence these helpers can ask for; ids, not i18n keys, travel through the helpers. */
type PasswordText =
  | PasswordProblem
  | 'usernameTaken'
  | 'usernameLocked'
  | 'notAllowed'
  | 'invalid'
  | 'tooCommon'
  | 'currentWrong'
  | 'locked'
  | 'changeRequired'
  | 'self'
  | 'signInRefused'
  | 'companyDeactivated'
  | 'passwordRequired'
  | 'accountExists'
  | 'hasEmail'
  | 'noEmailUnavailable'
  | 'holderAboveBasic'
  | 'needsEmail'

/** Refusal prefix -> sentence. No prefix is a prefix of another. */
export const REFUSAL_TEXTS: ReadonlyArray<readonly [string, PasswordText]> = [
  ['username_taken:', 'usernameTaken'],
  ['username_locked:', 'usernameLocked'],
  ['password_not_allowed:', 'notAllowed'],
  ['password_invalid:', 'invalid'],
  ['password_current_wrong:', 'currentWrong'],
  ['password_locked:', 'locked'],
  [PASSWORD_CHANGE_REQUIRED, 'changeRequired'],
  // Clearing a no-email holder's only way in, granting to somebody who already has a level or an address, and a database without no-email accounts.
  ['password_required:', 'passwordRequired'],
  ['password_holder:', 'holderAboveBasic'],
  ['password_needs_email:', 'needsEmail'],
  ['account_exists:', 'accountExists'],
  ['has_email:', 'hasEmail'],
  ['no_email_account_unavailable:', 'noEmailUnavailable'],
]

/** The level screen's sentence for a save refused over a password holder, or null to show the server's own detail. */
export function levelRefusalLine(error: unknown, t: (key: string) => string): string | null {
  if (!axios.isAxiosError(error)) return null
  const detail: unknown = error.response?.data?.detail
  if (typeof detail !== 'string') return null
  if (detail.startsWith('password_holder:')) return t('password.refusalLevelHolders')
  if (detail.startsWith('password_needs_email:')) return t('password.refusalLevelNeedsEmail')
  return null
}

/** The admin route's own-account 409 carries no prefix; matched on its fixed sentence. */
const SELF_REFUSAL = 'you cannot set your own password'

/** Which sentence explains this refusal, or null to fall back to `errorMessage(error)`. Reads only a STRING `detail`; a 422 list is not ours. */
export function refusalText(error: unknown): PasswordText | null {
  if (!axios.isAxiosError(error)) return null
  const detail = error.response?.data?.detail
  if (typeof detail !== 'string') return null
  if (detail.startsWith(SELF_REFUSAL)) return 'self'
  if (detail.startsWith('password_invalid:') && detail.includes('too common')) return 'tooCommon'
  for (const [prefix, text] of REFUSAL_TEXTS) {
    if (detail.startsWith(prefix)) return text
  }
  return null
}

/** The live checklist's line for each rule: literal `t()` calls for the same reason as `passwordText`. */
export function passwordRuleText(t: (key: string) => string, id: PasswordRuleId | 'common'): string {
  switch (id) {
    case 'length':
      return t('password.ruleLength')
    case 'lower':
      return t('password.ruleLower')
    case 'upper':
      return t('password.ruleUpper')
    case 'digit':
      return t('password.ruleDigit')
    case 'symbol':
      return t('password.ruleSymbol')
    case 'personal':
      return t('password.rulePersonal')
    case 'run':
      return t('password.ruleRun')
    case 'sequence':
      return t('password.ruleSequence')
    case 'common':
      return t('password.ruleCommon')
  }
}

/** A switch of LITERAL `t('password.…')` calls, not a key map, so `i18n/keys.test.ts`'s call-site sweep sees every key. */
export function passwordText(t: (key: string) => string, text: PasswordText): string {
  switch (text) {
    case 'tooShort':
      return t('password.problemTooShort')
    case 'tooLong':
      return t('password.problemTooLong')
    case 'isUsername':
      return t('password.problemIsUsername')
    case 'needLower':
      return t('password.problemNeedLower')
    case 'needUpper':
      return t('password.problemNeedUpper')
    case 'needDigit':
      return t('password.problemNeedDigit')
    case 'needSymbol':
      return t('password.problemNeedSymbol')
    case 'hasPersonal':
      return t('password.problemHasPersonal')
    case 'hasRun':
      return t('password.problemHasRun')
    case 'hasSequence':
      return t('password.problemHasSequence')
    case 'tooCommon':
      return t('password.refusalTooCommon')
    case 'mismatch':
      return t('password.problemMismatch')
    case 'usernameTaken':
      return t('password.refusalUsernameTaken')
    case 'usernameLocked':
      return t('password.refusalUsernameLocked')
    case 'notAllowed':
      return t('password.refusalNotAllowed')
    case 'invalid':
      return t('password.refusalInvalid')
    case 'currentWrong':
      return t('password.refusalCurrentWrong')
    case 'locked':
      return t('password.refusalLocked')
    case 'changeRequired':
      return t('password.forcedIntro')
    case 'self':
      return t('password.refusalSelf')
    case 'signInRefused':
      return t('password.signInRefused')
    case 'companyDeactivated':
      return t('password.refusalCompanyDeactivated')
    case 'passwordRequired':
      return t('password.refusalPasswordRequired')
    case 'accountExists':
      return t('password.refusalAccountExists')
    case 'hasEmail':
      return t('password.refusalHasEmail')
    case 'noEmailUnavailable':
      return t('password.refusalNoEmailUnavailable')
    case 'holderAboveBasic':
      return t('password.refusalHolderAboveBasic')
    case 'needsEmail':
      return t('password.refusalNeedsEmail')
  }
}

/** Every `PasswordText`, so the parity test can walk them all. */
export const ALL_PASSWORD_TEXTS: readonly PasswordText[] = [
  'tooShort',
  'tooLong',
  'isUsername',
  'needLower',
  'needUpper',
  'needDigit',
  'needSymbol',
  'hasPersonal',
  'hasRun',
  'hasSequence',
  'tooCommon',
  'mismatch',
  'usernameTaken',
  'usernameLocked',
  'notAllowed',
  'invalid',
  'currentWrong',
  'locked',
  'changeRequired',
  'self',
  'signInRefused',
  'companyDeactivated',
  'passwordRequired',
  'accountExists',
  'hasEmail',
  'noEmailUnavailable',
  'holderAboveBasic',
  'needsEmail',
]

/** The sign-in form's message for a failed attempt. Every 401 is the same sentence and nothing branches on its text, so the page cannot be used to learn which accounts exist.
 *  Anything that is not a 401 returns null and the caller shows `errorMessage`. */
export function signInFailure(error: unknown): PasswordText | null {
  if (axios.isAxiosError(error) && isCompanyDeactivated(error.response?.data?.detail)) return 'companyDeactivated'
  if (axios.isAxiosError(error) && error.response?.status === 401) return 'signInRefused'
  return null
}

/** The proof a customer administrator's password step hands back; absent for everyone else, whose session is already open. */
export function passwordChallenge(body: unknown): string | null {
  const challenge = (body as { challenge?: unknown } | null | undefined)?.challenge
  return typeof challenge === 'string' && challenge !== '' ? challenge : null
}

/** Which refusal the second step can explain. Keyed on the `detail` prefix: other routes answer 429 and 503 for other reasons. */
export function secondStepFailure(error: unknown): 'notSent' | 'tooMany' | null {
  if (!axios.isAxiosError(error)) return null
  const detail: unknown = error.response?.data?.detail
  if (typeof detail !== 'string') return null
  if (detail.startsWith('code_not_sent:')) return 'notSent'
  if (detail.startsWith('code_budget:')) return 'tooMany'
  return null
}

interface AdminRow {
  id: string
  tier: string | null
  is_active: boolean | null
  is_admin: boolean
  email?: string | null
  microsoft_linked?: boolean
}

/** Does the Users page offer «Password» on this row? An active, non-administrator account, not Microsoft-bound, that is not the admin's own row -- the server's `password_ineligibility`. */
export function passwordActionAvailable(user: AdminRow, selfId: string | null | undefined): boolean {
  if (passwordGrantAvailable(user)) return user.id !== selfId
  if (user.tier == null || user.is_active !== true) return false
  if (user.is_admin || user.microsoft_linked) return false
  return user.id !== selfId
}

/** The PUT body. `username` is sent only to fill a blank one; an existing one is an import key the route refuses to change (`username_locked:`). */
export function buildPasswordSetPayload(
  password: string,
  usernameInput: string,
  currentUsername: string | null | undefined,
): { password: string; username?: string } {
  const filled = (currentUsername ?? '').trim()
  const wanted = usernameInput.trim()
  if (!filled && wanted) return { password, username: wanted }
  return { password }
}


/** Can this row be given an account through «Password» (level, username and starting password in one step)? Only a person with NO account and NO email; the server refuses the other cases. */
export function passwordGrantAvailable(user: { tier: string | null; email?: string | null }): boolean {
  return user.tier == null && !(user.email ?? '').trim()
}

/** Which form the «Password» dialog opens on. */
export function passwordDialogMode(user: { tier: string | null; email?: string | null }): 'grant' | 'manage' {
  return passwordGrantAvailable(user) ? 'grant' : 'manage'
}

const UNGRANTABLE_LEVELS = ['admin', 'public']

/** `permissions.PASSWORD_ABILITIES`, plus the legacy words that read as nothing more. A wire contract. */
const PASSWORD_ABILITIES: ReadonlySet<string> = new Set([
  'basic',
  'self.log',
  'staff',
  'roster.self',
  'public.view',
  'member.view',
  'clients.edit',
  'pricelist.edit',
  'sales_person',
  'account.standard',
  'roster.manage',
  'orgchart.edit',
  'time.past_edit',
  'leave.override',
  'leave.finance_sign',
])

/** May a password sign in to this level? Unknown abilities fail closed. */
export function levelTakesPassword(level: { slug: string; abilities?: readonly string[] }): boolean {
  if (UNGRANTABLE_LEVELS.includes(level.slug) || !level.abilities) return false
  return level.abilities.every((ability) => PASSWORD_ABILITIES.has(ability))
}

/** The levels the grant forms offer: basic access at most (`password_not_allowed:`), never `public`, which only a share link holds (`public_level_locked:`). */
export function grantableLevels<T extends { slug: string; abilities?: readonly string[] }>(
  levels: readonly T[] | undefined,
): T[] {
  return (levels ?? []).filter(levelTakesPassword)
}

/** The `password-account` body, or null while the form cannot be sent. The route takes the username always and must REPEAT a set one, so a stored username wins over whatever is typed. */
export function buildPasswordGrantPayload(
  tier: string,
  password: string,
  usernameInput: string,
  currentUsername: string | null | undefined,
): { tier: string; username: string; password: string } | null {
  const username = (currentUsername ?? '').trim() || usernameInput.trim()
  if (!tier || !username || !password) return null
  return { tier, username, password }
}

/** Does removing this person's password also remove their access level? Yes for an account holder with no email: the server refuses a bare removal (`password_required:`) so the dialog sends `?revoke_access=true`. */
export function removeRevokesAccess(user: {
  tier: string | null
  email?: string | null
  has_password?: boolean
}): boolean {
  return Boolean(user.has_password) && user.tier != null && !(user.email ?? '').trim()
}

/** The DELETE URL, carrying `revoke_access` only when `removeRevokesAccess` says so. */
export function passwordRemoveUrl(user: {
  id: string
  tier: string | null
  email?: string | null
  has_password?: boolean
}): string {
  const base = `/api/admin/users/${user.id}/password`
  return removeRevokesAccess(user) ? `${base}?revoke_access=true` : base
}

/** How a signed-in person is named where an address would be: email, else username, else an empty string, never the literal `null`. */
export function accountHandle(user: { email?: string | null; username?: string | null }): string {
  return (user.email ?? '').trim() || (user.username ?? '').trim()
}

export type NoEmailField = 'firstName' | 'tier' | 'username' | 'password'

export interface NoEmailAccountInput {
  firstName: string
  lastName: string
  tier: string
  username: string
  password: string
}

/** Add user's «No email» form: the fields still wrong, in screen order, and the `POST /api/admin/users/password-account` body once there are none. Neither ungrantable level is a choice. */
export function noEmailAccountForm(input: NoEmailAccountInput): {
  failures: NoEmailField[]
  payload: { first_name: string; last_name: string; tier: string; username: string; password: string } | null
} {
  const firstName = input.firstName.trim()
  const lastName = input.lastName.trim()
  const username = input.username.trim()
  const failures: NoEmailField[] = []
  if (!firstName) failures.push('firstName')
  if (!input.tier || UNGRANTABLE_LEVELS.includes(input.tier)) failures.push('tier')
  if (!username) failures.push('username')
  const problem = passwordProblem(input.password, username || null, undefined, { names: [firstName, lastName] })
  if (!input.password || problem !== null) failures.push('password')
  if (failures.length > 0) return { failures, payload: null }
  return {
    failures,
    payload: { first_name: firstName, last_name: lastName, tier: input.tier, username, password: input.password },
  }
}
